// src/BlockedUsersPage.js - Admin view for blocked users
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  TextField as MuiTextField,
  InputAdornment,
} from '@mui/material';
import UnblockIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { brand, ink } from './designTokens';
import ProfileDialog from './ProfileDialog';

import { prettyStatus } from './labels';
const digits = (v) => String(v || '').replace(/\D/g, '');

export default function BlockedUsersPage() {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [allBlockedUsers, setAllBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogPhone, setDialogPhone] = useState(null);
  const navigate = useNavigate();
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [phoneToBlock, setPhoneToBlock] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all blocked users with profile info
      const { data, error: queryError } = await supabase
        .from('blocked_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      // Admin bans live in blocked_identities, one row per identity. Without
      // these the page would list only member-to-member blocks, and someone
      // banned from the dialog below could never be unbanned again.
      const { data: identityRows, error: identityError } = await supabase
        .from('blocked_identities')
        .select('*')
        .order('created_at', { ascending: false });
      if (identityError) console.error('Error fetching blocked identities:', identityError);

      if ((!data || data.length === 0) && (!identityRows || identityRows.length === 0)) {
        setBlockedUsers([]);
        setAllBlockedUsers([]);
        setLoading(false);
        return;
      }

      // Resolve BOTH sides of the block to real people. Previously only the
      // blocked user was looked up, so "Blocked By" rendered a raw phone
      // number — unreadable when the blocker is another member rather than
      // an admin.
      const allPhones = [...new Set([
        ...data.map(b => b.blocked_phone_number),
        ...data.map(b => b.blocker_phone_number),
      ].filter(Boolean))];

      const { data: profiles, error: profilesError } = await supabase
        .from('Profiles')
        .select('id, first_name, last_name, phone_number, email, profile_image, user_type')
        .in('phone_number', allPhones);

      if (profilesError) console.error('Error fetching profiles:', profilesError);

      // Phone formats drift between clients (spaces, dashes, missing +), so
      // match on digits rather than requiring an exact string equality.
      const byDigits = new Map((profiles || []).map(p => [digits(p.phone_number), p]));

      const enriched = (data || []).map(block => ({
        ...block,
        profile: byDigits.get(digits(block.blocked_phone_number)) || null,
        blockerProfile: byDigits.get(digits(block.blocker_phone_number)) || null,
      }));

      // One ban covers several identities, so they are grouped back into the
      // one person they belong to rather than listed four times.
      const { data: allProfiles } = await supabase
        .from('Profiles')
        .select('id, first_name, last_name, phone_number, email, profile_image, user_type, apple_user_id, google_user_id');

      const identityBans = new Map();
      for (const row of identityRows || []) {
        const owner = (allProfiles || []).find((pr) => {
          if (row.kind === 'phone') return digits(pr.phone_number).endsWith(row.value);
          if (row.kind === 'email') return (pr.email || '').trim().toLowerCase() === row.value;
          if (row.kind === 'apple') return (pr.apple_user_id || '').trim() === row.value;
          if (row.kind === 'google') return (pr.google_user_id || '').trim() === row.value;
          return false;
        });
        const key = owner?.id || `${row.kind}:${row.value}`;
        const existing = identityBans.get(key);
        if (existing) {
          existing.kinds.push(row.kind);
        } else {
          identityBans.set(key, {
            id: `identity-${key}`,
            isIdentityBan: true,
            profileId: owner?.id || null,
            kinds: [row.kind],
            created_at: row.created_at,
            reason: row.reason,
            blocker_phone_number: row.created_by || 'admin',
            blocked_phone_number: owner?.phone_number || row.value,
            profile: owner || null,
            blockerProfile: null,
          });
        }
      }

      const combined = [...identityBans.values(), ...enriched];
      setBlockedUsers(combined);
      setAllBlockedUsers(combined);
    } catch (err) {
      console.error('Error fetching blocked users:', err);
      setError(`Error: ${err.message || 'Failed to fetch blocked users'}`);
      setBlockedUsers([]);
      setAllBlockedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter by search term
  useEffect(() => {
    if (!searchTerm) {
      setBlockedUsers(allBlockedUsers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allBlockedUsers.filter(block => {
      const profile = block.profile;
      const matchesName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.toLowerCase().includes(term) : false;
      const matchesPhone = block.blocked_phone_number?.toLowerCase().includes(term);
      const matchesBlocker = block.blocker_phone_number?.toLowerCase().includes(term);
      const matchesEmail = profile?.email?.toLowerCase().includes(term);
      return matchesName || matchesPhone || matchesBlocker || matchesEmail;
    });

    setBlockedUsers(filtered);
  }, [searchTerm, allBlockedUsers]);

  const handleBlockUser = async () => {
    if (!phoneToBlock) {
      alert('Please enter a phone number');
      return;
    }

    setLoading(true);
    try {
      // Find the person by phone or by email. The old version matched
      // phone_number exactly, so '(212) 555-0125' came back "not found" while
      // the same number stored as '+12125550125' sat right there.
      const term = phoneToBlock.trim();
      const termDigits = digits(term);
      const { data: candidates, error: lookupError } = await supabase
        .from('Profiles')
        .select('id, phone_number, email, first_name, last_name');
      if (lookupError) throw lookupError;

      const profile = (candidates || []).find((row) => {
        if (termDigits.length >= 10 && digits(row.phone_number).endsWith(termDigits.slice(-10))) return true;
        return term.includes('@') &&
          (row.email || '').trim().toLowerCase() === term.toLowerCase();
      });

      if (!profile) {
        alert('No profile found for that phone number or email address.');
        setLoading(false);
        return;
      }

      // Ban the person, not one phone number: admin_ban_profile records every
      // identity the account signs in with, so they cannot come straight back
      // through Apple or Google with no phone attached.
      const { data: result, error: blockError } = await supabase
        .rpc('admin_ban_profile', { p_profile_id: profile.id, p_reason: blockReason || null });

      if (blockError) throw blockError;

      const kinds = result?.identities || [];
      const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'That account';
      alert(
        kinds.length
          ? `${name} is banned. Blocked sign-in by: ${kinds.join(', ')}.`
          : `${name} is banned, but the account carries no identity to block.`
      );
      setBlockDialogOpen(false);
      setPhoneToBlock('');
      setBlockReason('');
      await fetchData();
    } catch (err) {
      console.error('Error blocking user:', err);
      alert(`Error: ${err.message || 'Failed to block user'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockUser = async (blockedPhone, block) => {
    if (!window.confirm('Are you sure you want to unblock this user?')) return;

    setLoading(true);
    try {
      if (block?.isIdentityBan && block.profileId) {
        // Lifts every identity in one go, and the old-style admin row with
        // them, so the person is not left half-banned.
        const { error } = await supabase
          .rpc('admin_unban_profile', { p_profile_id: block.profileId });
        if (error) throw error;
        alert('User unblocked successfully');
        await fetchData();
        return;
      }

      const { error } = await supabase
        .from('blocked_users')
        .delete()
        .eq('blocked_phone_number', blockedPhone);

      if (error) throw error;

      alert('User unblocked successfully');
      await fetchData();
    } catch (err) {
      console.error('Error unblocking user:', err);
      alert(`Error: ${err.message || 'Failed to unblock user'}`);
    } finally {
      setLoading(false);
    }
  };

  const getProfileImageUrl = (profile) => {
    if (!profile) return null;
    if (profile.profile_image_url) return profile.profile_image_url;
    if (profile.profile_image) {
      const { data } = supabase.storage
        .from('profile-images')
        .getPublicUrl(`profiles/${profile.profile_image}`);
      return data.publicUrl;
    }
    return null;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Blocked Users ({blockedUsers.length})
        </Typography>
        <Button
          startIcon={<PersonAddIcon />}
          variant="contained"
          color="error"
          onClick={() => setBlockDialogOpen(true)}
        >
          Block User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <MuiTextField
          placeholder="Search by name, phone, email..."
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : blockedUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No blocked users found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>Blocked By</TableCell>
                <TableCell>Blocked Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blockedUsers.map((block) => {
                const profile = block.profile;
                const profileImageUrl = getProfileImageUrl(profile);
                return (
                  <TableRow key={block.id} hover>
                    <TableCell>
                      <Box
                        onClick={() => {
                          // Prefer the real profile page; fall back to the
                          // lightweight dialog when the person has no Profiles
                          // row (blocked before signup, or since deleted).
                          if (profile?.id) navigate(`/dashboard/profile/${profile.id}`);
                          else if (block.blocked_phone_number) setDialogPhone(block.blocked_phone_number);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          if (profile?.id) navigate(`/dashboard/profile/${profile.id}`);
                          else if (block.blocked_phone_number) setDialogPhone(block.blocked_phone_number);
                        }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          cursor: 'pointer',
                          '&:hover .blocked-name': { color: brand.gold, textDecoration: 'underline' },
                        }}
                      >
                        <Avatar
                          src={profileImageUrl}
                          sx={{ width: 32, height: 32 }}
                        >
                          {profile?.first_name?.[0] || '?'}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography className="blocked-name" variant="body2" sx={{ color: ink[900] }}>
                            {profile
                              ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unnamed member'
                              : 'Not a member'}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: ink[300] }}>
                            {block.blocked_phone_number}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{profile?.email || '-'}</TableCell>
                    <TableCell>
                      {profile?.user_type ? (
                        <Chip label={prettyStatus(profile.user_type)} size="small" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {block.blockerProfile
                        ? `${block.blockerProfile.first_name || ''} ${block.blockerProfile.last_name || ''}`.trim() || 'Unnamed member'
                        : (String(block.blocker_phone_number || '').toLowerCase() === 'admin'
                            ? 'Admin'
                            : block.blocker_phone_number || 'Admin')}
                    </TableCell>
                    <TableCell>
                      {block.created_at
                        ? new Date(block.created_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<UnblockIcon />}
                        color="success"
                        onClick={() => handleUnblockUser(block.blocked_phone_number, block)}
                      >
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Block User Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Phone number or email"
            value={phoneToBlock}
            onChange={(e) => setPhoneToBlock(e.target.value)}
            margin="normal"
            placeholder="Enter phone number to block"
          />
          <TextField
            fullWidth
            label="Reason (Optional)"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder="Reason for blocking this user..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBlockUser}
            variant="contained"
            color="error"
            disabled={!phoneToBlock || loading}
          >
            Block User
          </Button>
        </DialogActions>
      </Dialog>

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={() => setDialogPhone(null)}
      />
    </Box>
  );
}



